import { ArrowUpRight, BookOpen, DatabaseZap } from 'lucide-react';
import { Link } from 'react-router';

import { DataPanel } from '@/components/dataPanel';
import { SearchInput } from '@/components/filterChips';
import { EmptyState, StateView } from '@/components/stateViews';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCatalogQuery } from '@/hooks/useObservabilityQueries';
import { useRouteContext } from '@/hooks/useRouteContext';
import { formatInteger } from '@/lib/format';
import { matchesSearch } from '@/lib/search';

export const CatalogPage = () => {
  const { setState, state } = useRouteContext();
  const catalogQuery = useCatalogQuery(state);
  const definitions = (catalogQuery.data?.definitions ?? []).filter((definition) =>
    matchesSearch(state.search, [
      definition.key,
      definition.displayName,
      definition.description,
      definition.family,
      definition.source,
      definition.owner,
      ...(definition.supportedDimensions ?? []),
    ]),
  );
  const observedMetricSamples = catalogQuery.data?.metricSamples ?? [];
  const observedSampleCount = observedMetricSamples.reduce((total, metric) => total + (metric.samples ?? 0), 0);

  return (
    <DataPanel
      error={catalogQuery.error}
      isError={catalogQuery.isError}
      isLoading={catalogQuery.isLoading}
      loadingLabel="Loading governed metric definitions"
      onRetry={() => void catalogQuery.refetch()}
    >
      <div className="grid min-w-0 gap-5">
        <StateView
          title={`${formatInteger(catalogQuery.data?.definitions?.length)} governed metric definitions`}
          tone={(catalogQuery.data?.definitions?.length ?? 0) > 0 ? 'healthy' : 'coverage'}
        >
          Definitions describe meaning and calculation. Observed coverage for {state.env} and the selected window is reported separately.
        </StateView>

        <SearchInput
          onChange={(search) => setState({ search })}
          placeholder="Search metric meaning, source, owner, or dimension"
          value={state.search}
        />

        {definitions.length === 0 ? (
          <EmptyState
            title={state.search ? 'No metric definitions match this search' : 'Metric definitions are unavailable'}
            tone={state.search ? 'filtered' : 'coverage'}
          >
            {state.search
              ? 'Clear the definition search or search by metric key, family, source, owner, or supported dimension.'
              : 'The API returned no governed definitions. Observed samples below do not substitute for metric meaning or ownership.'}
          </EmptyState>
        ) : null}

        <section className="grid gap-4 xl:grid-cols-2">
          {definitions.map((definition) => {
            const dimensions = definition.supportedDimensions ?? [];
            const caveats = definition.caveats ?? [];
            const exploreSearch = new URLSearchParams({
              compareDimension: dimensions[0] ?? 'source',
              compareMetric: definition.key,
            });
            return (
              <Card key={definition.key}>
                <CardHeader className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-muted flex items-center gap-2 text-xs font-semibold">
                      <BookOpen aria-hidden="true" className="size-4" />
                      {definition.key}
                    </div>
                    <CardTitle className="mt-2">{definition.displayName}</CardTitle>
                  </div>
                  <Badge tone="blue">{definition.unit}</Badge>
                </CardHeader>
                <CardContent className="grid gap-4">
                  <p className="text-ink text-sm">{definition.description}</p>
                  <dl className="grid gap-3 text-sm sm:grid-cols-2">
                    {[
                      ['Family', definition.family],
                      ['Source', definition.source],
                      ['Grain', definition.grain],
                      ['Owner', definition.owner],
                      ['Formula', definition.formula],
                      ['Time basis', definition.timeBasis],
                      ['Freshness', definition.freshness],
                      ['Coverage', definition.coverage],
                    ].map(([label, value]) => (
                      <div className="border-line rounded-md border p-3" key={label}>
                        <dt className="text-muted text-xs font-semibold">{label}</dt>
                        <dd className="text-ink mt-1">{value}</dd>
                      </div>
                    ))}
                  </dl>
                  <div>
                    <p className="text-muted text-xs font-semibold">Supported dimensions</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {dimensions.map((dimension) => (
                        <Badge key={dimension} tone="slate">
                          {dimension.replaceAll('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  {caveats.length > 0 ? (
                    <details className="border-line rounded-md border">
                      <summary className="text-muted cursor-pointer px-3 py-2 text-xs font-semibold">Caveats</summary>
                      <ul className="border-line grid gap-2 border-t px-5 py-3 text-sm">
                        {caveats.map((caveat) => (
                          <li className="list-disc" key={caveat}>
                            {caveat}
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                  <Button asChild variant="secondary">
                    <Link to={{ pathname: '/explore', search: exploreSearch.toString() }}>
                      Explore this metric
                      <ArrowUpRight aria-hidden="true" className="size-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </section>

        <Card>
          <CardHeader className="flex items-center gap-2">
            <DatabaseZap aria-hidden="true" className="text-muted size-4" />
            <CardTitle>Observed scope coverage</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[
              ['Published metric names', observedMetricSamples.length],
              ['Published metric samples', observedSampleCount],
              ['Observed span names', catalogQuery.data?.spans?.length ?? 0],
              ['Observed Web Vital names', catalogQuery.data?.rumWebVitals?.length ?? 0],
            ].map(([label, value]) => (
              <div className="border-line rounded-md border p-3" key={String(label)}>
                <p className="text-muted text-xs font-semibold">{label}</p>
                <p className="text-ink numeric mt-2 text-lg font-semibold">{formatInteger(Number(value))}</p>
              </div>
            ))}
            <p className="text-muted text-xs sm:col-span-2 xl:col-span-4">
              These are observations in the current scope, not additional metric definitions and not proof of complete collection.
            </p>
          </CardContent>
        </Card>
      </div>
    </DataPanel>
  );
};
